import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';

import { Box, FieldSet, Heading, Label, LabeledControl, Legend, Paragraph, Radio } from '@zillow/constellation-10';

import { showModalNotification } from '@common/actions/NotificationActions';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import userPersonas from '@common/constants/userPersonas';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import { DEFAULT_ERROR } from '@rental-manager/constants/NotificationConstants';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  currentProperty: ZrmClickstreamListing;
  fromDashboard: boolean;
  goToSubmissionPage: () => void;
  onBack: () => void;
  onClose: () => void;
  phoneNumber: string;
  sendVerificationCode: ({ verificationMethod }) => Promise<any>;
  setVerificationMethodPhone: () => void;
  setVerificationMethodSms: () => void;
  user: ZrmClickstreamUser;
  verificationMethod: string;
}

const ChooseMethodPage = (props: Props) => {
  const {
    currentProperty,
    fromDashboard,
    goToSubmissionPage,
    onBack,
    onClose,
    phoneNumber,
    sendVerificationCode,
    setVerificationMethodPhone,
    setVerificationMethodSms,
    user,
    verificationMethod,
  } = props;
  const dispatch = useDispatch();

  const [{ loading: isLoading, error }, onSubmit] = useAsyncFn(async () => {
    const response = await sendVerificationCode({ verificationMethod });

    if (response && response.error) {
      throw new Error('Failed to select verification method');
    } else {
      if (!fromDashboard) {
        const trackingData = {
          currentProperty,
          user,
        };

        if (verificationMethod === 'sms') {
          IreneOnboardingCSEvents.emitContinueFromPhoneVerificationPageUsingTextEvent(trackingData);
        } else {
          IreneOnboardingCSEvents.emitContinueFromPhoneVerificationPageUsingCallEvent(trackingData);
        }
      }
      goToSubmissionPage();
    }
  }, [sendVerificationCode, verificationMethod, fromDashboard, currentProperty, user, goToSubmissionPage]);

  useEffect(() => {
    if (error) {
      dispatch(showModalNotification(DEFAULT_ERROR, { subject: 'Code', verb: 'sent' }));
    }
  }, [error]);

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={fromDashboard ? 'Edit deposit account' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <Heading level={3} textStyle="body-lg-bold">
            Verify your identity
          </Heading>
          <Paragraph textStyle="body-sm">
            {`To help keep your account safe, we’ll send a confirmation code to ${phoneNumber}. ${
              fromDashboard ? 'Enter it to continue editing your deposit account.' : ''
            }`}
          </Paragraph>
          <FieldSet legend={<Legend>How would you like to get your code?</Legend>}>
            <LabeledControl
              control={
                <Radio
                  name="verification-method"
                  checked={verificationMethod === 'sms'}
                  onChange={setVerificationMethodSms}
                  value="sms"
                />
              }
              label={<Label>Text it to me</Label>}
            />
            <LabeledControl
              control={
                <Radio
                  name="verification-method"
                  checked={verificationMethod === 'phone'}
                  onChange={setVerificationMethodPhone}
                  value="phone"
                />
              }
              label={<Label>Give me a call</Label>}
            />
          </FieldSet>
        </Box>
      }
      backAction={{ onClick: onBack }}
      footerAriaLabel="Phone verification actions"
      footerEnd={
        <DebouncedButton onClick={onSubmit} disabled={!verificationMethod || isLoading}>
          Continue
        </DebouncedButton>
      }
    />
  );
};

export default ChooseMethodPage;
